import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { z } from 'zod';

import { formatCanonicalServiceLabel } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_labels';
import { cn } from '@apps/web/lib/utils';
import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
    ArtifactMetaDisclosure,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';

type AdminVisualizationArtifactProps = {
    readonly artifact: AdminAgentArtifact;
};

type ParsedVisualizationPayload = z.infer<typeof visualizationPayloadSchema>;
type ChartDatum = Record<string, number | string | null>;

const RENDERABLE_CHART_TYPES = new Set([
    'line',
    'bar',
    'stacked_bar',
    'waterfall',
    'metric_card',
    'timeline',
    'dashboard',
]);
const MAX_RENDERED_CHART_CELLS = 5_000;
// Above this row count, always-on line markers read as noise, so series drop
// them and rely on activeDot for hover inspection.
const MAX_DOTTED_POINTS = 12;
const CHART_COLORS = [
    '#2563eb',
    '#16a34a',
    '#dc2626',
    '#9333ea',
    '#d97706',
    '#0891b2',
];

const scalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const visualizationAxisSchema = z.object({
    key: z.string().min(1).nullable().optional(),
    label: z.string().min(1),
    unit: z.string().min(1).nullable().optional(),
});

const visualizationDimensionSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    unit: z.string().min(1).nullable().optional(),
});

const visualizationSeriesSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    value_key: z.string().min(1),
    unit: z.string().min(1).nullable().optional(),
    source_ids: z.array(z.string()).optional().default([]),
    aggregation: z.string().min(1).nullable().optional(),
    stack: z.string().min(1).nullable().optional(),
});

const visualizationMetricSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    value: scalarSchema,
    unit: z.string().min(1).nullable().optional(),
    delta: z.union([z.string(), z.number()]).nullable().optional(),
    delta_label: z.string().min(1).nullable().optional(),
    source_ids: z.array(z.string()).optional().default([]),
});

const visualizationTimelineEventSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    kind: z.string().min(1),
    timestamp: z.string().nullable().optional(),
    start: z.string().nullable().optional(),
    end: z.string().nullable().optional(),
    status: z.string().min(1).nullable().optional(),
    value: scalarSchema.optional(),
    source_ids: z.array(z.string()).optional().default([]),
    url: z.string().min(1).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional().default({}),
});

const visualizationDashboardSectionSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    status: z.enum(['ok', 'warning', 'critical', 'unknown', 'unavailable']),
    summary: z.string().min(1).nullable().optional(),
    metrics: z.array(visualizationMetricSchema).optional().default([]),
    source_ids: z.array(z.string()).optional().default([]),
    caveats: z.array(z.string()).optional().default([]),
});

const visualizationRawDataLinkSchema = z.object({
    label: z.string().min(1),
    url: z.string().min(1),
    source_id: z.string().min(1).nullable().optional(),
});

const visualizationTimeWindowSchema = z.object({
    start: z.string().nullable().optional(),
    end: z.string().nullable().optional(),
    timezone: z.string().min(1).nullable().optional(),
});

const visualizationProvenanceSchema = z
    .object({
        source_ids: z.array(z.string()).optional().default([]),
        query: z.string().min(1).nullable().optional(),
        time_window: visualizationTimeWindowSchema.nullable().optional(),
        aggregation: z.string().min(1).nullable().optional(),
        row_count: z.number().int().nonnegative().nullable().optional(),
        row_limit: z.number().int().positive().nullable().optional(),
        sample_count: z.number().int().nonnegative().nullable().optional(),
        sample_limit: z.number().int().positive().nullable().optional(),
        raw_data_links: z
            .array(visualizationRawDataLinkSchema)
            .optional()
            .default([]),
    })
    .default({ raw_data_links: [], source_ids: [] });

const visualizationPayloadSchema = z.object({
    schema_version: z.literal(1).optional().default(1),
    chart_type: z.enum([
        'line',
        'bar',
        'stacked_bar',
        'scatter',
        'histogram',
        'waterfall',
        'graph',
        'metric_card',
        'timeline',
        'dashboard',
    ]),
    title: z.string().min(1).nullable().optional(),
    description: z.string().min(1).nullable().optional(),
    x_axis: visualizationAxisSchema.nullable().optional(),
    y_axis: visualizationAxisSchema.nullable().optional(),
    dimensions: z.array(visualizationDimensionSchema).optional().default([]),
    series: z.array(visualizationSeriesSchema).optional().default([]),
    data: z.array(z.record(z.string(), scalarSchema)).optional().default([]),
    metrics: z.array(visualizationMetricSchema).optional().default([]),
    timeline_events: z
        .array(visualizationTimelineEventSchema)
        .optional()
        .default([]),
    dashboard_sections: z
        .array(visualizationDashboardSectionSchema)
        .optional()
        .default([]),
    nodes: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    edges: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    provenance: visualizationProvenanceSchema,
    caveats: z.array(z.string()).optional().default([]),
});

export function AdminVisualizationArtifact({
    artifact,
}: AdminVisualizationArtifactProps) {
    const parsedPayload = visualizationPayloadSchema.safeParse(
        artifact.payload,
    );
    if (!parsedPayload.success) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <VisualizationStatus
                        artifact={artifact}
                        statusTone="error"
                        statusText="Malformed visualization"
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <VisualizationError>
                    This visualization payload does not match the admin
                    visualization contract.
                </VisualizationError>
            </ArtifactFrame>
        );
    }

    const payload = parsedPayload.data;
    const metadata = (
        <VisualizationMetadata artifact={artifact} payload={payload} />
    );
    if (!RENDERABLE_CHART_TYPES.has(payload.chart_type)) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <VisualizationStatus
                        artifact={artifact}
                        statusTone="warning"
                        statusText="Unsupported visualization type"
                    />
                }
                title={payload.title ?? artifact.title}
            >
                <ArtifactEvidence artifact={artifact} />
                <VisualizationError>
                    The admin UI cannot render {payload.chart_type} charts yet.
                </VisualizationError>
                {metadata}
            </ArtifactFrame>
        );
    }

    const validationError = validateRenderablePayload(payload);
    if (validationError) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <VisualizationStatus
                        artifact={artifact}
                        statusTone="error"
                        statusText={validationError.title}
                    />
                }
                title={payload.title ?? artifact.title}
            >
                <ArtifactEvidence artifact={artifact} />
                <VisualizationError>
                    {validationError.detail}
                </VisualizationError>
                {metadata}
            </ArtifactFrame>
        );
    }

    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <VisualizationStatus
                    artifact={artifact}
                    statusTone="ready"
                    statusText={formatChartType(payload.chart_type)}
                />
            }
            title={payload.title ?? artifact.title}
        >
            {payload.description ? (
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {payload.description}
                </p>
            ) : null}
            {payload.chart_type === 'metric_card' ? (
                <MetricCards payload={payload} />
            ) : payload.chart_type === 'waterfall' ? (
                <WaterfallVisualization payload={payload} />
            ) : payload.chart_type === 'timeline' ? (
                <TimelineVisualization payload={payload} />
            ) : payload.chart_type === 'dashboard' ? (
                <DashboardVisualization payload={payload} />
            ) : (
                <GenericChart payload={payload} />
            )}
            <ArtifactMetaDisclosure>
                <ArtifactEvidence artifact={artifact} />
                {metadata}
            </ArtifactMetaDisclosure>
        </ArtifactFrame>
    );
}

function GenericChart({
    payload,
}: {
    readonly payload: ParsedVisualizationPayload;
}) {
    const chartData = buildChartData(payload);
    return (
        <div className="h-64 min-h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                {payload.chart_type === 'line' ? (
                    <LineChart
                        data={chartData}
                        margin={{ top: 8, right: 18, bottom: 8, left: 4 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            minTickGap={18}
                        />
                        <YAxis tick={{ fontSize: 11 }} width={42} />
                        <Tooltip />
                        {payload.series.map((series, index) => (
                            <Line
                                key={series.id}
                                type="monotone"
                                dataKey={series.id}
                                name={series.label}
                                stroke={
                                    CHART_COLORS[index % CHART_COLORS.length]
                                }
                                strokeWidth={2}
                                dot={
                                    chartData.length > MAX_DOTTED_POINTS
                                        ? false
                                        : { r: 2 }
                                }
                                activeDot={{ r: 4 }}
                            />
                        ))}
                    </LineChart>
                ) : (
                    <BarChart
                        data={chartData}
                        margin={{ top: 8, right: 18, bottom: 8, left: 4 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            minTickGap={18}
                        />
                        <YAxis tick={{ fontSize: 11 }} width={42} />
                        <Tooltip />
                        {payload.series.map((series, index) => (
                            <Bar
                                key={series.id}
                                dataKey={series.id}
                                name={series.label}
                                fill={CHART_COLORS[index % CHART_COLORS.length]}
                                stackId={
                                    payload.chart_type === 'stacked_bar'
                                        ? (series.stack ?? 'stack')
                                        : undefined
                                }
                            />
                        ))}
                    </BarChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}

function MetricCards({
    payload,
}: {
    readonly payload: ParsedVisualizationPayload;
}) {
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {payload.metrics.map((metric) => (
                <div
                    key={metric.id}
                    className="rounded-[7px] border border-[#DBE3E3]/75 bg-white/70 px-3 py-2.5"
                >
                    <div className="text-muted-foreground text-[11px]">
                        {metric.label}
                    </div>
                    <div className="text-foreground mt-1 flex items-baseline gap-1.5">
                        <span className="text-lg font-semibold">
                            {formatScalar(metric.value)}
                        </span>
                        {metric.unit ? (
                            <span className="text-muted-foreground text-xs">
                                {metric.unit}
                            </span>
                        ) : null}
                    </div>
                    {metric.delta !== null && metric.delta !== undefined ? (
                        <div className="text-muted-foreground mt-1 text-[11px]">
                            {metric.delta_label ?? 'Delta'}:{' '}
                            {formatScalar(metric.delta)}
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function WaterfallVisualization({
    payload,
}: {
    readonly payload: ParsedVisualizationPayload;
}) {
    const labelKey =
        payload.x_axis?.key ?? payload.dimensions[0]?.key ?? 'label';
    const rows = payload.data.map((row, index) => {
        const values = payload.series.map((series) => ({
            series,
            value: numberValue(row[series.value_key]) ?? 0,
        }));
        const total =
            numberValue(row['total_tokens']) ??
            values.reduce((sum, item) => sum + item.value, 0);
        return {
            id: stringValue(row['id']) ?? `row-${index + 1}`,
            label: stringValue(row[labelKey]) ?? `Row ${index + 1}`,
            total,
            costUsd: numberValue(row['cost_usd']),
            sourceUrl: stringValue(row['source_url']),
            values,
        };
    });
    const maxTotal = Math.max(
        1,
        ...rows.map((row) => (Number.isFinite(row.total) ? row.total : 0)),
    );

    return (
        <div className="space-y-2">
            {rows.map((row) => (
                <div
                    key={row.id}
                    className="rounded-[7px] border border-[#DBE3E3]/75 bg-white/75 px-3 py-2.5"
                >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="min-w-0 text-xs font-medium text-[#263938]">
                            {row.sourceUrl && safeExternalUrl(row.sourceUrl) ? (
                                <a
                                    href={safeExternalUrl(row.sourceUrl) ?? '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex max-w-full items-center gap-1 truncate text-sky-700 hover:underline"
                                >
                                    <span className="truncate">
                                        {row.label}
                                    </span>
                                    <ExternalLink className="size-3 shrink-0" />
                                </a>
                            ) : (
                                row.label
                            )}
                        </div>
                        <div className="text-muted-foreground text-[11px]">
                            {formatScalar(row.total)} tokens
                            {row.costUsd !== null
                                ? ` · $${row.costUsd.toLocaleString('en-US', {
                                      maximumFractionDigits: 4,
                                  })}`
                                : null}
                        </div>
                    </div>
                    <div className="mt-2 flex h-3 overflow-hidden rounded bg-[#E7EEEE]">
                        {row.values.map(({ series, value }, index) => {
                            const width = Math.max(0, (value / maxTotal) * 100);
                            return (
                                <div
                                    key={series.id}
                                    aria-label={`${series.label}: ${formatScalar(
                                        value,
                                    )}`}
                                    className="h-full"
                                    style={{
                                        width: `${width}%`,
                                        backgroundColor:
                                            CHART_COLORS[
                                                index % CHART_COLORS.length
                                            ],
                                    }}
                                    title={`${series.label}: ${formatScalar(
                                        value,
                                    )}`}
                                />
                            );
                        })}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#5D706E]">
                        {row.values
                            .filter((item) => item.value > 0)
                            .map(({ series, value }) => (
                                <span key={series.id}>
                                    {series.label}: {formatScalar(value)}
                                </span>
                            ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TimelineVisualization({
    payload,
}: {
    readonly payload: ParsedVisualizationPayload;
}) {
    if (payload.timeline_events.length === 0) {
        return (
            <VisualizationEmptyState>
                No timeline events available.
            </VisualizationEmptyState>
        );
    }
    return (
        <ol className="space-y-2">
            {payload.timeline_events.map((event) => {
                const eventTime = event.timestamp ?? event.start ?? event.end;
                const safeUrl = event.url ? safeExternalUrl(event.url) : null;
                const metadataEntries = Object.entries(event.metadata).slice(
                    0,
                    4,
                );
                return (
                    <li
                        key={event.id}
                        className="rounded-[7px] border border-[#DBE3E3]/75 bg-white/75 px-3 py-2.5"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <VisualizationPill tone="neutral">
                                        {formatChartType(event.kind)}
                                    </VisualizationPill>
                                    {event.status ? (
                                        <VisualizationPill
                                            tone={statusTone(event.status)}
                                        >
                                            {event.status}
                                        </VisualizationPill>
                                    ) : null}
                                </div>
                                <div className="mt-1 text-xs font-medium text-[#263938]">
                                    {safeUrl ? (
                                        <a
                                            href={safeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex max-w-full items-center gap-1 text-sky-700 hover:underline"
                                        >
                                            <span className="truncate">
                                                {event.label}
                                            </span>
                                            <ExternalLink className="size-3 shrink-0" />
                                        </a>
                                    ) : (
                                        event.label
                                    )}
                                </div>
                            </div>
                            <div className="text-muted-foreground shrink-0 text-[11px]">
                                {eventTime
                                    ? formatDateTime(eventTime)
                                    : 'No time'}
                            </div>
                        </div>
                        {metadataEntries.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-[#5D706E]">
                                {metadataEntries.map(([key, value]) => (
                                    <span
                                        key={key}
                                        className="rounded bg-[#F0F5F5] px-2 py-0.5"
                                    >
                                        {key}: {formatScalar(value)}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

function DashboardVisualization({
    payload,
}: {
    readonly payload: ParsedVisualizationPayload;
}) {
    return (
        <div className="space-y-3">
            {payload.metrics.length > 0 ? (
                <MetricCards payload={payload} />
            ) : null}
            <div className="grid gap-2 md:grid-cols-2">
                {payload.dashboard_sections.map((section) => (
                    <div
                        key={section.id}
                        className="rounded-[7px] border border-[#DBE3E3]/75 bg-white/75 px-3 py-2.5"
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="text-xs font-medium text-[#263938]">
                                    {section.label}
                                </div>
                                {section.summary ? (
                                    <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
                                        {section.summary}
                                    </p>
                                ) : null}
                            </div>
                            <VisualizationPill
                                tone={statusTone(section.status)}
                            >
                                {section.status}
                            </VisualizationPill>
                        </div>
                        {section.metrics.length > 0 ? (
                            <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                                {section.metrics.map((metric) => (
                                    <div
                                        key={metric.id}
                                        className="rounded bg-[#F0F5F5] px-2 py-1.5"
                                    >
                                        <div className="text-muted-foreground text-[10px]">
                                            {metric.label}
                                        </div>
                                        <div className="text-xs font-medium text-[#263938]">
                                            {formatScalar(metric.value)}
                                            {metric.unit
                                                ? ` ${metric.unit}`
                                                : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {section.caveats.length > 0 ? (
                            <p className="text-muted-foreground mt-2 text-[11px] leading-relaxed">
                                {section.caveats.join(' ')}
                            </p>
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

function VisualizationEmptyState({
    children,
}: {
    readonly children: ReactNode;
}) {
    return (
        <div className="rounded-[7px] border border-[#DBE3E3]/75 bg-white/75 px-3 py-2 text-xs text-[#5D706E]">
            {children}
        </div>
    );
}

function VisualizationPill({
    children,
    tone,
}: {
    readonly children: ReactNode;
    readonly tone: 'neutral' | 'ok' | 'warning' | 'critical' | 'unavailable';
}) {
    const className =
        tone === 'critical'
            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300'
            : tone === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300'
              : tone === 'ok'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300'
                : tone === 'unavailable'
                  ? 'border-slate-200 bg-slate-50 text-slate-600'
                  : 'border-[#DBE3E3] bg-[#F0F5F5] text-[#5D706E]';
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[10px] font-medium',
                className,
            )}
        >
            {children}
        </span>
    );
}

function VisualizationStatus({
    artifact,
    statusTone,
    statusText,
}: {
    readonly artifact: AdminAgentArtifact;
    readonly statusTone: 'ready' | 'warning' | 'error';
    readonly statusText: string;
}) {
    return (
        <Fragment>
            <ArtifactLifecycleStatusBadge
                lifecycleStatus={artifact.lifecycle_status}
            />
            <span
                className={
                    statusTone === 'error'
                        ? 'text-xs font-medium text-red-700 dark:text-red-300'
                        : statusTone === 'warning'
                          ? 'text-xs font-medium text-amber-700 dark:text-amber-300'
                          : 'text-muted-foreground text-xs'
                }
            >
                {statusText}
            </span>
        </Fragment>
    );
}

function VisualizationMetadata({
    artifact,
    payload,
}: {
    readonly artifact: AdminAgentArtifact;
    readonly payload: ParsedVisualizationPayload;
}) {
    const provenance = payload.provenance;
    return (
        <div className="space-y-2 rounded-[7px] border border-[#DBE3E3]/70 bg-[#F0F5F5]/45 px-3 py-2.5">
            <dl className="grid gap-x-4 gap-y-1.5 text-[11px] sm:grid-cols-[6rem_1fr]">
                <MetadataRow label="Sources">
                    {provenance.source_ids.length > 0
                        ? formatSourceIdList(provenance.source_ids)
                        : 'Not provided'}
                </MetadataRow>
                <MetadataRow label="Time window">
                    {formatTimeWindow(provenance.time_window) ??
                        formatArtifactTimeWindow(artifact.time_window) ??
                        'Not provided'}
                </MetadataRow>
                <MetadataRow label="Aggregation">
                    {provenance.aggregation ?? seriesAggregations(payload)}
                </MetadataRow>
                <MetadataRow label="Rows">
                    {formatCountLimit(
                        provenance.row_count ?? payload.data.length,
                        provenance.row_limit,
                    )}
                    {provenance.sample_count !== undefined ||
                    provenance.sample_limit !== undefined
                        ? ` · sample ${formatCountLimit(
                              provenance.sample_count,
                              provenance.sample_limit,
                          )}`
                        : null}
                </MetadataRow>
                <MetadataRow label="Query">
                    {provenance.query ? (
                        <code className="break-words">{provenance.query}</code>
                    ) : (
                        'Not provided'
                    )}
                </MetadataRow>
                <MetadataRow label="Caveats">
                    {payload.caveats.length > 0
                        ? payload.caveats.join(' ')
                        : 'None'}
                </MetadataRow>
            </dl>
            {provenance.raw_data_links.length > 0 ? (
                <div className="flex flex-wrap gap-2 text-[11px]">
                    {provenance.raw_data_links.map((link) => {
                        const safeUrl = safeExternalUrl(link.url);
                        if (!safeUrl) {
                            return (
                                <span
                                    key={`${link.label}-${link.url}`}
                                    className="text-muted-foreground rounded bg-white/75 px-2 py-1"
                                >
                                    {link.label}
                                </span>
                            );
                        }
                        return (
                            <a
                                key={`${link.label}-${safeUrl}`}
                                href={safeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded bg-white/80 px-2 py-1 text-sky-700 hover:underline"
                            >
                                <ExternalLink className="size-3" />
                                {link.label}
                            </a>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function MetadataRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}) {
    return (
        <Fragment>
            <dt className="text-muted-foreground/70">{label}</dt>
            <dd className="text-muted-foreground min-w-0 break-words">
                {children}
            </dd>
        </Fragment>
    );
}

function VisualizationError({ children }: { readonly children: ReactNode }) {
    return (
        <div className="flex items-start gap-2 rounded-[7px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>{children}</span>
        </div>
    );
}

function validateRenderablePayload(
    payload: ParsedVisualizationPayload,
): { readonly title: string; readonly detail: string } | null {
    if (payload.chart_type === 'metric_card') {
        return payload.metrics.length === 0
            ? {
                  title: 'Malformed visualization',
                  detail: 'Metric-card visualizations require at least one metric.',
              }
            : null;
    }
    if (payload.chart_type === 'timeline') {
        return null;
    }
    if (payload.chart_type === 'dashboard') {
        return payload.dashboard_sections.length === 0 &&
            payload.metrics.length === 0
            ? {
                  title: 'Malformed visualization',
                  detail: 'Dashboard visualizations require at least one metric or section.',
              }
            : null;
    }
    if (payload.series.length === 0 || payload.data.length === 0) {
        return {
            title: 'Malformed visualization',
            detail: 'Chart visualizations require at least one series and one data row.',
        };
    }
    if (
        payload.series.length * payload.data.length >
        MAX_RENDERED_CHART_CELLS
    ) {
        return {
            title: 'Visualization too large',
            detail: `This chart has more than ${MAX_RENDERED_CHART_CELLS.toLocaleString()} renderable cells. Use the raw-data link or aggregate the data further.`,
        };
    }
    const missingSeries = payload.series.find((series) =>
        payload.data.every(
            (row) => numberValue(row[series.value_key]) === null,
        ),
    );
    if (missingSeries) {
        return {
            title: 'Malformed visualization',
            detail: `Series "${missingSeries.label}" does not have numeric data under "${missingSeries.value_key}".`,
        };
    }
    return null;
}

function buildChartData(
    payload: ParsedVisualizationPayload,
): Array<ChartDatum> {
    const xKey =
        payload.x_axis?.key ??
        payload.dimensions[0]?.key ??
        Object.keys(payload.data[0] ?? {})[0] ??
        'label';
    return payload.data.map((row, index) => {
        const datum: ChartDatum = {
            label: stringValue(row[xKey]) ?? `Row ${index + 1}`,
        };
        for (const series of payload.series) {
            datum[series.id] = numberValue(row[series.value_key]);
        }
        return datum;
    });
}

function seriesAggregations(payload: ParsedVisualizationPayload): string {
    const aggregations = [
        ...new Set(
            payload.series
                .map((series) => series.aggregation)
                .filter((value): value is string => !!value),
        ),
    ];
    return aggregations.length > 0 ? aggregations.join(', ') : 'Not provided';
}

function formatChartType(value: string): string {
    return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatScalar(value: unknown): string {
    if (typeof value === 'number') {
        return value.toLocaleString('en-US');
    }
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    return 'None';
}

function formatCountLimit(
    count: number | null | undefined,
    limit: number | null | undefined,
): string {
    const countText = count === null || count === undefined ? 'unknown' : count;
    return limit === null || limit === undefined
        ? String(countText)
        : `${countText} / ${limit}`;
}

function formatTimeWindow(
    value: ParsedVisualizationPayload['provenance']['time_window'],
): string | null {
    if (!value) {
        return null;
    }
    const start = value.start
        ? formatDateTime(value.start, value.timezone ?? undefined)
        : null;
    const end = value.end
        ? formatDateTime(value.end, value.timezone ?? undefined)
        : null;
    const timezone = value.timezone ? ` ${value.timezone}` : '';
    if (start && end) {
        return `${start} - ${end}${timezone}`;
    }
    return start ?? end ?? null;
}

function formatArtifactTimeWindow(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }
    return formatTimeWindow({
        start: stringValue(value['start']) ?? stringValue(value['from']),
        end: stringValue(value['end']) ?? stringValue(value['to']),
        timezone: stringValue(value['timezone']),
    });
}

function formatDateTime(value: string, timeZone?: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...(timeZone ? { timeZone } : {}),
    };
    try {
        return date.toLocaleString(undefined, options);
    } catch {
        return date.toLocaleString(undefined, {
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            month: 'short',
        });
    }
}

function safeExternalUrl(value: string): string | null {
    const trimmedValue = value.trim();
    if (trimmedValue.startsWith('/') && !trimmedValue.startsWith('//')) {
        return trimmedValue;
    }
    try {
        const url = new URL(trimmedValue);
        return url.protocol === 'http:' || url.protocol === 'https:'
            ? url.toString()
            : null;
    } catch {
        return null;
    }
}

function stringValue(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

function numberValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim()) {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
}

function formatSourceIdList(sourceIds: ReadonlyArray<string>): string {
    return sourceIds.map(formatCanonicalServiceLabel).join(', ');
}

function statusTone(
    value: string,
): 'neutral' | 'ok' | 'warning' | 'critical' | 'unavailable' {
    const normalized = value.toLowerCase();
    if (
        normalized === 'ok' ||
        normalized === 'healthy' ||
        normalized === 'checked' ||
        normalized === 'recovered'
    ) {
        return 'ok';
    }
    if (
        normalized === 'warning' ||
        normalized === 'warn' ||
        normalized === 'partial' ||
        normalized === 'other'
    ) {
        return 'warning';
    }
    if (
        normalized === 'critical' ||
        normalized === 'error' ||
        normalized === 'failed' ||
        normalized === 'alert' ||
        normalized === 'fired'
    ) {
        return 'critical';
    }
    if (normalized === 'unavailable' || normalized === 'skipped') {
        return 'unavailable';
    }
    return 'neutral';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
